import { t } from 'i18next';
import _ from 'lodash';
import { sqWorkbenchStore } from '@/core/core.stores';
import { PUSH_IGNORE } from '@/core/flux.service';
import { runFormula as compileFormulaOrThrowError } from '@/formula/formula.utilities';
import { resetItemFinder } from '@/itemFinder/itemFinder.actions';
import { setTableDefinitionEditorVisible } from '@/main/layout.actions';
import { goTo } from '@/main/routing.utilities';
import { setDatasources } from '@/search/search.actions';
import { GraphQL } from '@/serverSideTable/materializedTables/materializedTables.constants';
import { fetchDistinctValuesForColumn } from '@/serverSideTable/materializedTables/queries';
import { fetchTable } from '@/tableBuilder/tableBuilder.actions';
import { NULL_PLACEHOLDER } from '@/tableBuilder/tableBuilder.constants';
import { resetAssetBuilder } from '@/tableDefinitionEditor/assetBuilder.actions';
import { ColumnTypeOptions } from '@/tableDefinitionEditor/columnRules/columnRule.constants';
import {
  columnRuleOutputToColumnRuleInput,
  getRuleTypeFromRuleInput,
} from '@/tableDefinitionEditor/columnRules/columnRule.utilities';
import {
  ADD_TO_TREE_PSEUDO_RULE,
  CREATE_TREE_PSEUDO_RULE,
  ColumnRulesWithMetaData,
  RULES_WITH_PERMISSIONS,
} from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.constants';
import { ensureRuleHasName } from '@/tableDefinitionEditor/configureColumn.utilities';
import { extractChildKeyFromString } from '@/tableDefinitionEditor/eventPropertyAccessor.utilities';
import {
  getExistingTableDefinition,
  resetTableDefinition,
  setTableDefinition,
} from '@/tableDefinitionEditor/tableDefinition.actions';
import {
  CSV_TYPES,
  DATUM_ID_COLUMN,
  DEFAULT_PROPERTIES_FOR_ALL_ITEM_COLUMNS,
  EDITOR_TYPES,
  GARBAGE_OBJECT_CHAR,
  ID_COLUMN,
} from '@/tableDefinitionEditor/tableDefinition.constants';
import { resetTableEditorStore, setTableId } from '@/tableDefinitionEditor/tableDefinitionEditor.actions';
import { getAllItems } from '@/trend/trendDataHelper.utilities';
import { exposedForTesting } from '@/trendData/trend.actions';
import { ORIGINAL_NAME_KEY } from '@/trendData/trendData.constants';
import { fullyArchiveDatasource } from '@/utilities/datasources.utilities';
import { errorToast } from '@/utilities/toast.utilities';
import { addTableDefinition } from '@/workbook/workbook.actions';
import { setDisplayTableDefinitionEditor } from '@/worksheet/worksheet.actions';
import { sqDatasourcesApi } from '@seeqdev/api/endpoint/DatasourcesApi';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqMaterializedTablesApi } from '@seeqdev/api/endpoint/MaterializedTablesApi';
import {
  ModifyColumnsInTableDefinitionRecomputeColumnTypeEnum,
  sqTableDefinitionsApi,
} from '@seeqdev/api/endpoint/TableDefinitionsApi';
import { ColumnTypeEnum } from '@seeqdev/api/model/ColumnDefinitionInputV1';
import { TableTypeEnum } from '@seeqdev/api/model/TableDefinitionInputV1';
import { SeeqNames } from '@seeqdev/constants';
import { faFont } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { fcCapsuleSet, fcMetric, fcScalar, fcSeries } from '@seeqdev/icons/seeq';
import type { FormulaErrorInterface } from '@/formula/formula.types';
import type { SelectedSearchItem } from '@/search/search.types';
import type {
  ColumnRule,
  ColumnRuleInput,
  CombinedColumnRuleInputParameters,
} from '@/tableDefinitionEditor/columnRules/columnRule.constants';
import type { ColumnRuleWithMetadata } from '@/tableDefinitionEditor/columnRules/columnRuleBuilder.types';
import type { RuleControl } from '@/tableDefinitionEditor/hooks/useRuleState.hook';
import type {
  BooleanTableCell,
  ColumnDefinition,
  EditorType,
  FormulaCompileResult,
  ItemTableCell,
  MaterializedTableHeader,
  NumericTableCell,
  ProcessedMaterializedTable,
  ProcessedTableCell,
  RowDataColumnDefinition,
  TextTableCell,
  TreeHierarchyNode,
} from '@/tableDefinitionEditor/tableDefinition.types';
import type { ColDef, Column, ColumnState, GridApi } from '@seeqdev/ag/grid';
import type { ColumnDefinitionInputV1 } from '@seeqdev/api/model/ColumnDefinitionInputV1';
import type { ColumnDefinitionOutputV1 } from '@seeqdev/api/model/ColumnDefinitionOutputV1';
import type { ColumnRuleFormulaCreatorInputV1 } from '@seeqdev/api/model/ColumnRuleFormulaCreatorInputV1';
import type { ColumnRuleInputV1 } from '@seeqdev/api/model/ColumnRuleInputV1';
import type { DatasourceOutputV1 } from '@seeqdev/api/model/DatasourceOutputV1';
import type { ItemSearchPreviewV1 } from '@seeqdev/api/model/ItemSearchPreviewV1';
import type { RowIdInputV1 } from '@seeqdev/api/model/RowIdInputV1';
import type { RowRulesResultV1 } from '@seeqdev/api/model/RowRulesResultV1';
import type { RulesResultV1 } from '@seeqdev/api/model/RulesResultV1';
import type { TableComputeOutputV1 } from '@seeqdev/api/model/TableComputeOutputV1';
import type { TableDefinitionInputV1 } from '@seeqdev/api/model/TableDefinitionInputV1';
import type { TableDefinitionOutputV1 } from '@seeqdev/api/model/TableDefinitionOutputV1';
import type { ColumnDefinitionBulkEditInputV1 } from '@seeqdev/api/model/models';
import type { IconDefinition } from '@seeqdev/icons';

/**
 * Returns the search item column definition for shortcut operations (ancestor, descendant).
 * For CSV tables, uses CsvItemIdColumn; otherwise uses ItemIdColumn.
 * Falls back to the first column if the expected column is not found.
 */
export function searchItemColumnDefinition(
  tableDefinitionOutput: TableDefinitionOutputV1,
): ColumnDefinitionOutputV1 & { displayName: string } {
  const tableType = tableDefinitionOutput.additionalProperties?.find(
    (property) => property.name === SeeqNames.Properties.TableType,
  )?.value;
  const isCSVTable = tableType === TableTypeEnum.SCALINGCSV || tableType === TableTypeEnum.ITEMCSV;
  const searchItemsColumnName = isCSVTable
    ? SeeqNames.MaterializedTables.CsvItemIdColumn
    : SeeqNames.MaterializedTables.ItemIdColumn;
  const searchItemsColumn =
    tableDefinitionOutput.columnDefinitions.find((col) => col.columnName === searchItemsColumnName) ||
    tableDefinitionOutput.columnDefinitions[0];
  return { ...searchItemsColumn, displayName: 'Search Items' } as ColumnDefinitionOutputV1 & { displayName: string };
}

export function rowRulesResultKey(result: RowRulesResultV1): string {
  return `${result.itemId.toUpperCase()}:${(result.datumId ?? '').toUpperCase()}`;
}

/** Build an ag-grid row id from compute-result row identifiers, matching TableEditorGrid getRowId. */
export function rowIdFromRowRulesResult(result: RowRulesResultV1): string {
  const { itemId, datumId } = result;
  if (itemId && datumId) {
    return `${itemId}-${datumId}`;
  }
  return itemId;
}

/**
 * Normalize an ag-grid row id to a canonical casing. Row ids come from item/datum uuids whose casing
 * can differ between the materialized table and the compute results, so both the grid `getRowId` and any
 * store-side `getRowNode` lookups must agree on this normalization or node resolution silently fails.
 */
export function normalizeTableRowId(rowId: string): string {
  return rowId.toUpperCase();
}

/**
 * Compute the ag-grid row id for a leaf (non-group) row from its itemId and datumId, mirroring the composition
 * used by `TableEditorGrid.molecule.tsx::getRowId`. Callers must pass `undefined` for `datumId` when the DatumId
 * column is NOT present in ag-grid's row data — scaling / item tables filter it out via `getColumnsNotIncludedInAgGrid`,
 * so their row nodes carry an itemId-only id. Only CSV table types keep DatumId in ag-grid.
 */
export function computeAgGridLeafRowId(itemId: string | undefined, datumId: string | undefined): string | undefined {
  if (itemId && datumId) {
    return normalizeTableRowId(`${itemId}-${datumId}`);
  }
  if (itemId) {
    return normalizeTableRowId(itemId);
  }
  if (datumId) {
    return normalizeTableRowId(datumId);
  }
  return undefined;
}

/** Build the lookup key for a cell keyed by ag-grid row id and column id. Shared by every per-cell cache. */
export function cellKey(rowId: string, columnId: string): string {
  return `${normalizeTableRowId(rowId)}|${columnId.toLowerCase()}`;
}

export function cellEvaluationResultsCacheKey(rowId: string, columnId: string): string {
  return cellKey(rowId, columnId);
}

/**
 * Build a fresh cell evaluation results cache from a compute response. Any previously cached results are
 * discarded, and every column present in the response contributes its per-cell rule results to the cache.
 */
export function buildCellEvaluationResultsCache(
  incoming: Record<string, RowRulesResultV1[]>,
): Record<string, RulesResultV1[]> {
  const cache: Record<string, RulesResultV1[]> = {};

  for (const [columnId, results] of Object.entries(incoming)) {
    for (const result of results) {
      const rowId = rowIdFromRowRulesResult(result);
      cache[cellEvaluationResultsCacheKey(rowId, columnId)] = result.ruleResults;
    }
  }

  return cache;
}

export function getCellEvaluationResults(
  cellEvaluationResultsCache: Record<string, RulesResultV1[]>,
  rowId: string,
  columnId: string,
): RulesResultV1[] | undefined {
  return cellEvaluationResultsCache[cellEvaluationResultsCacheKey(rowId, columnId)];
}

export function mergeColumnEvaluationResults(
  existing: RowRulesResultV1[] | undefined,
  incoming: RowRulesResultV1[] | undefined,
): RowRulesResultV1[] | undefined {
  if (!incoming?.length) {
    return existing;
  }

  const merged = new Map<string, RowRulesResultV1>();
  existing?.forEach((result) => merged.set(rowRulesResultKey(result), result));
  incoming.forEach((result) => merged.set(rowRulesResultKey(result), result));
  return [...merged.values()];
}

export function mergeComputeResults(
  existing: Record<string, RowRulesResultV1[] | undefined> | undefined,
  incoming: Record<string, RowRulesResultV1[] | undefined> | undefined,
): Record<string, RowRulesResultV1[]> {
  const merged: Record<string, RowRulesResultV1[]> = {};

  for (const [columnId, results] of Object.entries(existing ?? {})) {
    if (results?.length) {
      merged[columnId.toLowerCase()] = results;
    }
  }

  for (const [columnId, incomingResults] of Object.entries(incoming ?? {})) {
    const normalizedColumnId = columnId.toLowerCase();
    merged[normalizedColumnId] =
      mergeColumnEvaluationResults(merged[normalizedColumnId], incomingResults) ?? incomingResults ?? [];
  }

  return merged;
}

export const getRuleTypeAndParameters = (
  columnRuleInput: ColumnRuleInput,
): {
  ruleType: ColumnRule;
  parameters: Partial<CombinedColumnRuleInputParameters>;
} => {
  const ruleType = getRuleTypeFromRuleInput(columnRuleInput);
  const parameters = columnRuleInput[ruleType]!;
  return { ruleType, parameters };
};

export const getColumnTypeFromText = (text: string): ColumnTypeEnum | undefined => {
  const columnType = ColumnTypeOptions.find((option) => t(option.label) === text);
  return columnType?.value;
};

export const getColumnRuleWithMetaDataFromLabel = (label: string): ColumnRuleWithMetadata | undefined =>
  ColumnRulesWithMetaData.find((rule) => rule.label === label);

export const getColumnRuleWithMetaDataFromRule = (rule: string): ColumnRuleWithMetadata => {
  // In the case of Constant rules, the rule comes back like "stringConstant" whereas the ruleWithLabel is just
  // "constant", causing the exception to be thrown. Instead, we match on whether the rule contains "constant" as a
  // substring
  const columnRule = ColumnRulesWithMetaData.find(
    (ruleWithLabel) =>
      ruleWithLabel.rule === rule ||
      (ruleWithLabel.rule === 'constant' && rule.toLowerCase().includes(ruleWithLabel.rule.toLowerCase())),
  );
  if (!columnRule) {
    throw new Error(`Column rule ${rule} not found`);
  }
  return columnRule;
};

export const buildRowDataForAgGrid = (
  agGridColumnDefinitions: RowDataColumnDefinition[],
  materializedTable: ProcessedMaterializedTable | undefined,
  tableType: TableTypeEnum,
  idsToFilterOut?: string[],
) => {
  if (!materializedTable) {
    return null;
  }

  const columnHeaderForSearchColumn = CSV_TYPES.includes(tableType)
    ? SeeqNames.MaterializedTables.CsvItemIdColumn
    : SeeqNames.MaterializedTables.ItemIdColumn;

  const materializedTableHeaders = materializedTable.headers;
  const indexOfItemIdColumn = materializedTableHeaders.findIndex(
    (header) => header.name === columnHeaderForSearchColumn,
  );
  const rows = idsToFilterOut
    ? materializedTable.rows.filter((row) => {
        // CSV tables can have null in their search column, so we need to check if the row has a valid item ID
        const cell = row[indexOfItemIdColumn];
        const uuid = !cell || !isItemTableCell(cell) ? undefined : cell?.uuid;
        return uuid ? !idsToFilterOut?.includes(uuid) : true;
      })
    : materializedTable.rows;
  return rows.map((row) => {
    const rowData: Record<string, any> = {};
    materializedTableHeaders.forEach((header: MaterializedTableHeader, index: number) => {
      // Match by column id first: it is stable across renames, whereas header.name reflects the persisted name and
      // can lag behind an in-progress preview rename (see previewTableColumns) until the rename is saved. Falling
      // back to a name match keeps this working for header shapes that don't carry an id.
      const agColumn = agGridColumnDefinitions.find(
        (columnDef) => (header.id && columnDef.field === header.id) || columnDef.headerName === header.name,
      );
      const field = agColumn?.field;
      if (field) {
        rowData[field] = row[index];
      }
    });
    return rowData;
  });
};

export const tableDefinitionOutputToTableDefinitionInput = (
  tableDefinition: TableDefinitionOutputV1,
  _scopedTo?: string,
): TableDefinitionInputV1 => {
  return {
    name: tableDefinition.name,
    description: tableDefinition.description,
    dataId: tableDefinition.dataId,
    datasourceClass: tableDefinition.datasourceClass,
    datasourceId: tableDefinition.datasourceId,
    scopedTo: tableDefinition.scopedTo,
    subscriptionId: tableDefinition.subscription?.id,
    batchAction: tableDefinition.batchAction,
    columnDefinitions: tableDefinition.columnDefinitions.map((columnDef) =>
      columnDefinitionOutputToColumnDefinitionInput(columnDef, tableDefinition.columnDefinitions),
    ),
    tableType: tableDefinition.tableType,
  };
};

export const columnDefinitionOutputToColumnDefinitionInput = (
  columnDefinition: ColumnDefinitionOutputV1,
  otherColumns: ColumnDefinitionOutputV1[],
): ColumnDefinitionBulkEditInputV1 => {
  return {
    columnId: columnDefinition.id,
    columnType: columnDefinition.columnType,
    columnUom: columnDefinition.columnUom,
    columnName: columnDefinition.columnName,
    columnRules: columnDefinition.rules
      .filter((rule) => !rule.rule.includes(SeeqNames.MaterializedTables.Rules.Overrides.Override))
      .map((rule) => columnRuleOutputToColumnRuleInput(rule, otherColumns)),
    isIndexed: columnDefinition.isIndexed,
    isHidden: columnDefinition.isHidden,
    sortAscending: columnDefinition.sortAscending,
    sortIndex: columnDefinition.sortIndex,
    isGenerated: columnDefinition.isGenerated,
    description: columnDefinition.description,
    displayProperty: columnDefinition.displayProperty,
    isManuallyEntered: columnDefinition.isManuallyEntered,
    overrideOutputItemMetadata: columnDefinition.outputItemTypeOverridden,
    outputItemType: columnDefinition.outputItemTypeOverridden ? columnDefinition.outputItemType : undefined,
    outputItemMetadata: columnDefinition.outputItemTypeOverridden ? columnDefinition.outputItemMetadata : undefined,
  };
};

export interface MutatedScalingItemSnapshot {
  id: string;
  tableId: string | undefined;
  tableName: string | undefined;
  columnName: string | undefined;
  rowName: string | undefined;
  rowId: string | undefined;
  columnId: string | undefined;
}

export const updateDetailsPaneItems = async ({
  tableDefinitionId,
  columnIds,
}: {
  tableDefinitionId: string;
  columnIds: string[];
}): Promise<MutatedScalingItemSnapshot[]> => {
  if (columnIds.length === 0) {
    return [];
  }

  // remove the props from item when column is deleted
  const detailsPaneItems = getAllItems({});
  const columnIdSet = new Set(columnIds);
  const itemsToUpdate = detailsPaneItems.filter(
    (item) => item.tableId === tableDefinitionId && item.columnId && columnIdSet.has(item.columnId),
  );
  const snapshots = itemsToUpdate.map((item) => ({
    id: item.id,
    tableId: item.tableId,
    tableName: item.tableName,
    columnName: item.columnName,
    rowName: item.rowName,
    rowId: item.rowId,
    columnId: item.columnId,
  }));

  itemsToUpdate.forEach((item) => {
    exposedForTesting.setTrendItemProps(
      item.id,
      {
        tableId: undefined,
        tableName: undefined,
        columnName: undefined,
        rowName: undefined,
        rowId: undefined,
        columnId: undefined,
      },
      PUSH_IGNORE,
    );
  });

  if (itemsToUpdate.length > 0) {
    await fetchTable();
  }

  return snapshots;
};

export const restoreDetailsPaneItems = async (snapshots: MutatedScalingItemSnapshot[]) => {
  if (snapshots.length === 0) {
    return;
  }

  snapshots.forEach((snapshot) => {
    exposedForTesting.setTrendItemProps(
      snapshot.id,
      {
        tableId: snapshot.tableId,
        tableName: snapshot.tableName,
        columnName: snapshot.columnName,
        rowName: snapshot.rowName,
        rowId: snapshot.rowId,
        columnId: snapshot.columnId,
      },
      PUSH_IGNORE,
    );
  });

  await fetchTable();
};

export const removeColumnDefinition = async ({
  tableDefinitionId,
  columnId,
  forceDelete = false,
}: {
  tableDefinitionId: string;
  columnId: string;
  forceDelete?: boolean;
}) => {
  const { data: tableDefinitionOutput } = await sqTableDefinitionsApi.deleteColumnFromTableDefinition({
    id: tableDefinitionId,
    columnId,
    forceDelete,
  });

  return tableDefinitionOutput;
};

export const removeColumnOverrides = async (tableDefinitionId: string, columnDefinitionId: string) => {
  const { data: tableDefinitionOutput } = await sqTableDefinitionsApi.deleteOverridesFromColumn({
    id: tableDefinitionId,
    columnId: columnDefinitionId,
  });
  return tableDefinitionOutput;
};

export const addOrUpdateColumnDefinition = async (
  tableDefinitionId: string,
  columnDefinition: ColumnDefinitionBulkEditInputV1,
  columnDefinitionId?: string,
  skipRuleCompute = false,
  includeDetailedResults = true,
): Promise<TableComputeOutputV1 | undefined> => {
  columnDefinition = {
    ...columnDefinition,
    columnId: columnDefinitionId,
  };
  const addOrUpdatePromise = sqTableDefinitionsApi.modifyColumnsInTableDefinition(
    { columnDefinitions: [columnDefinition] },
    {
      id: tableDefinitionId,
      recomputeColumnType: skipRuleCompute
        ? ModifyColumnsInTableDefinitionRecomputeColumnTypeEnum.NONE
        : ModifyColumnsInTableDefinitionRecomputeColumnTypeEnum.PROVIDEDWITHDEPENDENTS,
      includeDetailedResults,
    },
    { cancellationGroup: tableDefinitionId },
  );

  try {
    const { data: tableComputeOutput } = await addOrUpdatePromise;

    if (columnDefinition.columnRules && columnDefinition.columnRules.some((rule) => rule.treePathCreator)) {
      setDatasources('main', sqWorkbenchStore.stateParams.workbookId, true);
    }
    return tableComputeOutput;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

/**
 * When updating, this function will update properties of the table definition except for columns
 */
export const createOrUpdateTableDefinition = async (
  tableDefinition: TableDefinitionInputV1,
  tableDefinitionId?: string,
): Promise<TableDefinitionOutputV1> => {
  const { data: tableDefinitionOutput } = await (tableDefinitionId
    ? sqTableDefinitionsApi.updateTableDefinition(tableDefinition, { id: tableDefinitionId })
    : sqTableDefinitionsApi.createTableDefinition(tableDefinition));
  addTableDefinition(tableDefinitionOutput);

  return tableDefinitionOutput;
};

export const updateTableDefinitionName = async ({
  tableDefinitionId,
  name,
}: {
  tableDefinitionId: string;
  name: string;
}) => await sqItemsApi.setProperty({ value: name }, { id: tableDefinitionId, propertyName: SeeqNames.Properties.Name });

export const deleteTableRows = async (tableDefinitionId: string, rowIds: object[]) => {
  try {
    const response = await sqMaterializedTablesApi.deleteRows(
      {
        rows: rowIds as RowIdInputV1[],
      },
      {
        tableId: tableDefinitionId,
      },
    );
    return response.data;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
    return {};
  }
};

/**
 * Validates a formula for an instance of a formulaCreatorRule. In order to compile the formula, this function will
 * find the first viable item in each column that the formula references and use those as parameters. If no viable
 * items are found, the function will return an error. If viable parameters are found but there is a compilation
 * error, the compilation error will be returned.
 *
 * Uses {@link fetchDistinctValuesForColumn} per parameter column so that hidden columns (absent from the in-memory
 * materialized table) are still resolved, and any row in the table — not just the first — can supply the sample item.
 * Sample items are fetched directly from the backend, so this does not depend on any in-memory (CSRM) table state.
 */
export const validateFormulaForCreator = async (
  formulaRule: ColumnRuleFormulaCreatorInputV1,
  tableDefinitionId: string,
  columnDefinitions?: ColumnDefinitionOutputV1[],
): Promise<FormulaCompileResult> => {
  if (!columnDefinitions || columnDefinitions.length === 0) {
    return {
      success: false,
      errors: [{ message: 'No items in the table to run the formula on', column: -1, line: -1 }],
    };
  }
  const errors: FormulaErrorInterface[] = [];
  const parameters = formulaRule.parameters || [];
  const columnIndexes = formulaRule.columnIndexes || [];

  const parametersForCompile = (
    await Promise.all(
      parameters.map(async (parameter, index) => {
        const indexOfColumnInTableDefinition = columnIndexes[index] - 1;
        const columnName = columnDefinitions[indexOfColumnInTableDefinition].columnName;
        const values = await fetchDistinctValuesForColumn<string>({
          tableDefinitionId,
          columnName,
          limit: 1,
          filter: { simpleFilter: { columnName, filterType: GraphQL.FilterTypes.IsNotNull } },
        });
        const idOfFirstValidItemInGivenColumn = values[0];

        if (idOfFirstValidItemInGivenColumn === undefined && formulaRule.formula.includes(parameter)) {
          errors.push({
            message: `No valid items in column ${columnName} which is referred to by parameter: ${parameter}`,
            column: -1,
            line: -1,
          });
        }
        return idOfFirstValidItemInGivenColumn ? `${parameter}=${idOfFirstValidItemInGivenColumn}` : null;
      }),
    )
  ).filter((param): param is string => param !== null);

  if (errors.length > 0) {
    return { success: false, errors };
  }

  let formulaResult = formulaRule.formula;
  formulaRule.variableParameters?.forEach((parameter, index) => {
    const variableParams = formulaRule.variableParameterStrings
      ?.at(index)
      ?.split(',')
      ?.map((param) => param.trim())
      .map((param) => param.replace('$', ''))
      .filter((param) => parametersForCompile.some((paramForCompile) => paramForCompile.includes(`${param}=`)))
      .map((param) => `$${param}`);
    formulaResult = `$${parameter}=parameters(${variableParams?.join(',')})\n${formulaResult}`;
  });
  try {
    await compileFormulaOrThrowError(formulaResult, parametersForCompile);
    return { success: true, errors: [] };
  } catch (e: any) {
    return { success: false, errors: e };
  }
};

export async function editTableDefinition(tableDefinition: SelectedSearchItem, editorType: EditorType) {
  resetItemFinder();
  resetTableDefinition(editorType);
  resetTableEditorStore(editorType);
  try {
    setTableId(editorType, tableDefinition.id);
    const tableDefinitionOutput = await getExistingTableDefinition(tableDefinition.id, editorType);
    if (!tableDefinitionOutput) {
      return;
    }
    if (editorType === 'SCALING') {
      setDisplayTableDefinitionEditor(true);
    }
    return tableDefinitionOutput;
  } catch (e) {
    if (editorType === 'ITEM') {
      throw e;
    } else {
      setTableId(editorType, '');
      errorToast({ httpResponseOrError: e });
    }
  }
}

export const isItemTableCell = (cell: any): cell is ItemTableCell => {
  return cell && cell.uuid && typeof cell.uuid === 'string';
};

export const isNumericTableCell = (cell: any): cell is NumericTableCell => {
  return cell && typeof cell.value === 'number';
};

export const isTextTableCell = (cell: any): cell is TextTableCell => {
  return cell && typeof cell.value === 'string';
};

export const isBooleanTableCell = (cell: any): cell is BooleanTableCell => {
  return cell && typeof cell.value === 'boolean';
};

/**
 * Resolves the display value of a materialized table cell for use as a name, regardless of the underlying
 * column type (item/UUID, text, numeric, boolean). Returns undefined if the cell has no resolvable value.
 * CRAB-59914: used to resolve a scaling table row's navigation column value whether it is a UUID (item) or a
 * TEXT column, instead of assuming an item cell's `fetchedProperties`.
 */
export const getTableCellDisplayValue = (
  cell: ProcessedTableCell | undefined,
  displayProperty: string = SeeqNames.Properties.Name,
): string | undefined => {
  if (!cell) return undefined;
  if (isItemTableCell(cell)) return cell.fetchedProperties?.[displayProperty];
  if (isTextTableCell(cell)) return cell.value;
  if (isNumericTableCell(cell)) return _.isNil(cell.value) ? undefined : `${cell.value}${cell.uom ?? ''}`;
  if (isBooleanTableCell(cell)) return cell.value === undefined ? undefined : String(cell.value);
  return undefined;
};

export const isProcessedTableCell = (cell: unknown): cell is ProcessedTableCell => {
  return typeof cell === 'object' && cell !== null && 'override' in cell && typeof cell.override === 'boolean';
};

/**
 * Reduces a processed group-column cell to the scalar group key the materialized-table query groups by: item cells
 * become their `uuid`, other cells their stringified `value`, and a blank/null cell (or a cell with a null/undefined
 * value) becomes `null`. Shared by the AG Grid `keyCreator` and the SSRM drill-down `normalizeGroupKey` so a group's
 * key round-trips to the same value it was queried with.
 */
export const groupKeyFromCell = (cell: ProcessedTableCell | null | undefined): string | null => {
  if (cell == null) {
    return null;
  }
  if (isItemTableCell(cell)) {
    return cell.uuid;
  }
  const { value } = cell as NumericTableCell | TextTableCell | BooleanTableCell;
  return value === undefined || value === null ? null : String(value);
};

export const isColumnDefinition = (value: any): value is ColumnDefinition => {
  return (
    typeof value === 'object' && value !== null && 'displayName' in value && 'id' in value && 'columnName' in value
  );
};

/**
 * Retrieves the data source for a table definition.
 */
export const getTableDefinitionDataSource = async (
  tableDefinitionId: string,
): Promise<DatasourceOutputV1 | undefined> => {
  const {
    data: { datasources },
  } = await sqDatasourcesApi.getDatasources({
    datasourceId: tableDefinitionId.toLocaleLowerCase(),
  });

  return datasources?.at(0);
};

/**
 * Deletes datasource associated with a table definition which
 * will delete all created items associated with the table definition.
 */
export async function deleteTableItems(tableDefinitionId: string): Promise<void> {
  const tableDefinitionDatasource = await getTableDefinitionDataSource(tableDefinitionId);
  if (tableDefinitionDatasource) {
    await fullyArchiveDatasource(tableDefinitionDatasource.id);
  }
}

export const getColumnsContainingRulesWithPermissions = (
  columns: ColumnDefinitionOutputV1[],
): ColumnDefinitionOutputV1[] =>
  columns.filter((column) => column.rules.some((rule) => RULES_WITH_PERMISSIONS.includes(rule.rule)));

export const addOrUpdateColumnDefinitions = async (
  columnsToUpdate: ColumnDefinitionBulkEditInputV1[],
  tableDefinitionId: string,
  skipRuleCompute = false,
  includeDetailedResults = true,
): Promise<TableComputeOutputV1 | undefined> => {
  if (_.size(columnsToUpdate) <= 0) {
    return undefined;
  }
  try {
    const { data: tableComputeOutput } = await sqTableDefinitionsApi.modifyColumnsInTableDefinition(
      { columnDefinitions: columnsToUpdate },
      {
        id: tableDefinitionId,
        recomputeColumnType: skipRuleCompute
          ? ModifyColumnsInTableDefinitionRecomputeColumnTypeEnum.NONE
          : ModifyColumnsInTableDefinitionRecomputeColumnTypeEnum.PROVIDEDWITHDEPENDENTS,
        includeDetailedResults,
      },
      { cancellationGroup: tableDefinitionId },
    );

    return tableComputeOutput;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

const updateColumnRuleScope = (columnRules: ColumnRuleInputV1[], scopedTo?: string): ColumnRuleInputV1[] => {
  return columnRules.map((columnRule) => {
    const ruleType = Object.keys(columnRule)[0] as keyof ColumnRuleInputV1;
    if (RULES_WITH_PERMISSIONS.includes(ruleType)) {
      return {
        [ruleType]: {
          ...columnRule[ruleType],
          scopedTo,
        },
      };
    }
    return columnRule;
  });
};

/**
 * Updates the columns containing rules with permissions with the new
 * access settings
 */
export const updateColumnsContainingRulesWithScope = async (
  tableDefinition: TableDefinitionOutputV1,
  editorType: EditorType,
  workbookId?: string,
): Promise<TableDefinitionOutputV1 | undefined> => {
  const columnsToUpdate = getColumnsContainingRulesWithPermissions(tableDefinition.columnDefinitions).map((column) =>
    columnDefinitionOutputToColumnDefinitionInput(column, tableDefinition.columnDefinitions),
  );
  const columnsToUpdateWithUpdatedRules = columnsToUpdate.map((column) => ({
    ...column,
    columnRules: updateColumnRuleScope(column.columnRules, tableDefinition.scopedTo),
  }));

  if (columnsToUpdateWithUpdatedRules.some((column) => column.columnRules.some((rule) => rule.treePathCreator))) {
    await setDatasources('main', workbookId, true);
  }

  try {
    const columnUpdate = await addOrUpdateColumnDefinitions(
      columnsToUpdateWithUpdatedRules,
      tableDefinition.id,
      false,
      true,
    );

    return _.isEmpty(columnsToUpdateWithUpdatedRules) ? tableDefinition : columnUpdate?.tableDefinition;
  } catch (e) {
    errorToast({ httpResponseOrError: e });
  }
};

/**
 * Updates the visibility of columns in a table based on user actions.
 *
 * @param tableDefinitionId
 * @param existingColumnsAsOutputs
 * @param existingColumnsAsInputs
 * @param agColumns - The AgGrid columns pulled from the api at time of execution
 * @param setTableDefinition - set the table definition in the store
 * @param editorType - the editor type of the table
 * @returns Promise<void>
 */
export const onColumnHideTriggeredByUser = async (
  tableDefinitionId: string,
  existingColumnsAsOutputs: ColumnDefinition[],
  existingColumnsAsInputs: ColumnDefinitionInputV1[],
  agColumns: Column[],
  setTableDefinition: (editorType: EditorType, tableDefinitionOutput: TableDefinitionOutputV1) => void,
  editorType: EditorType,
) => {
  const columnIdToColumnInputMap: Record<string, ColumnDefinitionInputV1> = existingColumnsAsOutputs.reduce(
    (acc, column, index) => {
      acc[column.id] = existingColumnsAsInputs[index];
      return acc;
    },
    {} as Record<string, ColumnDefinitionInputV1>,
  );

  const tableColumnDefinitionsWithUpdatedVisibility: ColumnDefinitionBulkEditInputV1[] = (
    agColumns
      .map((agColumn) => {
        const columnId = agColumn.getColDef().field;
        const columnInput = columnId !== undefined ? columnIdToColumnInputMap[columnId] : undefined;
        const visibilityChanged = columnInput && !columnInput?.isHidden !== agColumn.isVisible();
        return visibilityChanged
          ? {
              isVisibleNow: agColumn.isVisible(),
              columnInput,
              columnId,
            }
          : undefined;
      })
      .filter((agColumn) => !!agColumn) as {
      isVisibleNow: boolean;
      columnInput: ColumnDefinitionInputV1;
      columnId: string;
    }[]
  ).map((agColumn) => {
    const columnInput = agColumn.columnInput;
    return {
      ...columnInput,
      isHidden: !agColumn.isVisibleNow,
      columnId: agColumn.columnId,
    } as ColumnDefinitionBulkEditInputV1;
  });

  const updatedTableDefinition = await addOrUpdateColumnDefinitions(
    tableColumnDefinitionsWithUpdatedVisibility,
    tableDefinitionId,
    true,
    false,
  );
  if (updatedTableDefinition) {
    setTableDefinition(editorType, updatedTableDefinition.tableDefinition);
  }
};

/**
 * Updates the column definitions based on the user's sorting changes in the table.
 *
 * @param tableDefinitionId
 * @param existingColumnsAsOutputs
 * @param existingColumnsAsInputs
 * @param agColumns The AgGrid columns pulled from the api at time of execution
 * @param editorType - the editor type of the table
 */
export const onSortChangedByUser = async (
  tableDefinitionId: string,
  existingColumnsAsOutputs: ColumnDefinition[],
  existingColumnsAsInputs: ColumnDefinitionInputV1[],
  agColumns: Column[],
  editorType: EditorType,
) => {
  const columnIdToColumnInputMap: Record<string, ColumnDefinitionInputV1> = existingColumnsAsOutputs.reduce(
    (acc, column, index) => {
      acc[column.id] = existingColumnsAsInputs[index];
      return acc;
    },
    {} as Record<string, ColumnDefinitionInputV1>,
  );
  const tableDefColumnsWithUpdatedSort = agColumns
    .map((agColumn) => {
      const columnId = agColumn.getColDef().field;
      const columnInput = columnId !== undefined ? columnIdToColumnInputMap[columnId] : undefined;
      const existingSortIndex = columnInput?.sortIndex;
      const existingPotentialSort = columnInput?.sortAscending ? 'asc' : 'desc';
      const existingSort = existingSortIndex !== undefined ? existingPotentialSort : undefined;
      const agSort = agColumn.getSort();
      const agSortIndex = agColumn.getSortIndex() ?? undefined;
      if (existingSort !== agSort || existingSortIndex !== agSortIndex) {
        return {
          ...columnInput,
          sortAscending: agSort === 'asc',
          sortIndex: agSortIndex,
          columnId,
        } as ColumnDefinitionBulkEditInputV1;
      }
      return undefined;
    })
    .filter((column) => !!column) as ColumnDefinitionBulkEditInputV1[];
  const updatedTableDefinition = await addOrUpdateColumnDefinitions(
    tableDefColumnsWithUpdatedSort,
    tableDefinitionId,
    true,
  );
  if (updatedTableDefinition) {
    setTableDefinition(editorType, updatedTableDefinition.tableDefinition);
  }
};

export const onColumnMovedByUser = async (
  tableDefinitionId: string,
  columns: ColumnDefinition[],
  agGridColumns: Column[],
  affectedColumnId: string,
  toIndex: number,
  editorType: EditorType,
) => {
  const columnsIdsNotInGrid = columns
    .filter((column) => !agGridColumns.find((agGridColumn) => agGridColumn.getColDef().field === column.id))
    .map((column) => column.id);

  const columnsIdsInGridInNewOrder = agGridColumns
    .map((agColumn) => agColumn.getColDef().field)
    .filter((columnId) => columnId !== affectedColumnId)
    .filter((columnId): columnId is string => columnId !== undefined);

  columnsIdsInGridInNewOrder.splice(toIndex, 0, affectedColumnId);
  const newOrder = columnsIdsNotInGrid.concat(columnsIdsInGridInNewOrder);
  const { data: updatedTableDefinition } = await sqTableDefinitionsApi.reorderColumnsInTableDefinition(
    { columnDefinitionIds: newOrder },
    { id: tableDefinitionId },
  );
  setTableDefinition(editorType, updatedTableDefinition);
};

export const getColumnInputBasedOnColumnName = (
  tableDefinition: TableDefinitionInputV1,
  name?: string,
): ColumnDefinitionInputV1 | undefined => {
  return tableDefinition.columnDefinitions.find((column) => column.columnName === name);
};

export const updateSortsInTheGrid = (tableDefinition: TableDefinitionOutputV1, agGridApi: GridApi) => {
  const columnStateForUpdatedSorts: ColumnState[] = tableDefinition.columnDefinitions
    .map((columnDefinition) => {
      const agColumn = agGridApi.getColumn(columnDefinition.id);
      const sortIndexFromAgColumn = agColumn?.getSortIndex();
      const sortDirection = agColumn?.getSort();
      const sortDirectionIsAscending = sortDirection === 'asc';

      const sortAscendingIsEqual = sortDirectionIsAscending === columnDefinition.sortAscending;
      const sortIndexIsEqual = sortIndexFromAgColumn === columnDefinition.sortIndex;
      if (!sortIndexIsEqual || !sortAscendingIsEqual) {
        const sortingIsOff = columnDefinition.sortIndex === undefined;
        const potentialSortDirection = columnDefinition.sortAscending ? 'asc' : 'desc';
        const sort = sortingIsOff ? null : potentialSortDirection;
        return {
          colId: columnDefinition.id,
          sort,
          sortIndex: columnDefinition.sortIndex ?? null,
        };
      } else {
        return undefined;
      }
    })
    .filter((col) => !!col) as ColumnState[];
  if (columnStateForUpdatedSorts.length > 0) {
    agGridApi.applyColumnState({ state: columnStateForUpdatedSorts });
  }
};

export const updateVisibilityInTheGrid = (tableDefinition: TableDefinitionOutputV1, agGridApi: GridApi) => {
  const columnStateForVisibilityChanged: ColumnState[] = tableDefinition.columnDefinitions
    .map((columnDefinition) => {
      const agColumn = agGridApi.getColumn(columnDefinition.id);
      const isHidden = !agColumn?.isVisible();
      if (columnDefinition.isHidden !== isHidden) {
        return {
          colId: columnDefinition.id,
          hide: columnDefinition.isHidden,
        };
      } else {
        return undefined;
      }
    })
    .filter((col) => !!col) as ColumnState[];
  if (columnStateForVisibilityChanged.length > 0) {
    agGridApi.applyColumnState({ state: columnStateForVisibilityChanged });
  }
};

export const generateTableName = (
  currentId: string,
  tablesInLocal: ItemSearchPreviewV1[],
  defaultName: string,
  currentName?: string,
): string => {
  if (!currentId && !currentName) {
    return `${t(defaultName)} ${tablesInLocal.length + 1}`;
  }
  return currentName || `${t(defaultName)} ${tablesInLocal.length + 1}`;
};

export const closeTableEditor = (editor: EditorType) => {
  if (editor === EDITOR_TYPES.ITEM) {
    goTo('/tree-builder');
  } else {
    setDisplayTableDefinitionEditor(false);
    setTableDefinitionEditorVisible(false);
    resetAssetBuilder();
    resetTableDefinition(editor);
    resetItemFinder();
    resetTableEditorStore(editor);
  }
};

/**
 * Uses {@link RuleControl} to construct the final rule state and outputs the rule being edited if the save is
 * successful, otherwise undefined. Updates ruleControl to facilitate changes in the rule UI throughout the
 * component stack.
 * The returned {@link ColumnRuleInputV1} needs to be
 * saved to local state or otherwise consumed, as state will be cleared at the end of a successful operation.
 */
export const createOrUpdateColumnRule = async (
  ruleControl: RuleControl,
  tableDefinitionId: string,
  allColumnDefinitions: ColumnDefinition[],
  skipFormulaValidation: boolean,
  savePseudoRuleRef: React.MutableRefObject<() => Promise<void>>,
  columnName: string,
): Promise<ColumnRuleInputV1 | undefined> => {
  const {
    canSave,
    columnRuleWithMetadata,
    ruleParametersState,
    setShowError,
    setColumnRuleWithMetadata,
    ruleParametersDispatch,
    setFormulaCompileResults,
  } = ruleControl;
  if (!canSave) {
    setShowError(true);
    return undefined;
  }
  await savePseudoRuleRef.current?.();
  let columnRule: ColumnRuleInputV1 = {
    [columnRuleWithMetadata!.rule]: ruleParametersState,
  };

  if (
    columnRuleWithMetadata!.rule === CREATE_TREE_PSEUDO_RULE ||
    columnRuleWithMetadata!.rule === ADD_TO_TREE_PSEUDO_RULE
  ) {
    columnRule = {
      treePathCreator: ruleParametersState,
    } as ColumnRuleInputV1;
  }

  const formulaCompileResults = await (columnRuleWithMetadata!.rule ===
    SeeqNames.MaterializedTables.Rules.FormulaCreator && !skipFormulaValidation
    ? validateFormulaForCreator(
        ruleParametersState as ColumnRuleFormulaCreatorInputV1,
        tableDefinitionId,
        allColumnDefinitions,
      )
    : Promise.resolve({ success: true, errors: [] }));

  setFormulaCompileResults(formulaCompileResults);
  if (!formulaCompileResults.success) {
    return undefined;
  }

  if (columnRule) {
    columnRule = ensureRuleHasName(columnRule, columnName);
  }

  ruleParametersDispatch('reset');
  setShowError(false);
  setColumnRuleWithMetadata(undefined);
  return columnRule;
};

/**
 * Returns the icon for the given types (type + uom) for AG grid column menu display. If all types are the same, it
 * returns the need ag grid icon element for that type.
 * @param types
 */
export const getIconForTypes = (types: { type: string | undefined; uom: any }[]): IconDefinition | undefined => {
  if (types.length > 0 && types.every((type) => type.type === types[0].type && type.uom === types[0].uom)) {
    return getIconForType(types[0].type, types[0].uom);
  }
  return undefined;
};

/**
 * Returns the icon classes for the given type and uom.
 * @param type
 * @param uom
 */
export const getIconForType = (type: string | undefined, uom: any): IconDefinition | undefined => {
  switch (type) {
    case SeeqNames.Types.CalculatedSignal:
    case SeeqNames.Types.StoredSignal:
      return uom !== 'string' ? fcSeries : faFont;
    case SeeqNames.Types.CalculatedCondition:
    case SeeqNames.Types.StoredCondition:
      return fcCapsuleSet;
    case SeeqNames.Types.LiteralScalar:
    case SeeqNames.Types.CalculatedScalar:
      return fcScalar;
    case SeeqNames.Types.ThresholdMetric:
      return fcMetric;
    default:
      return undefined;
  }
};

/**
 * Extracts the parent level from an event property rule string. The string should contain at most one instance of
 * parent[<parentLevel>].
 * @param string
 */
export const extractAncestorLevelFromString = (string: string) => {
  const ancestorRegex = new RegExp('parent\\[(\\d+)]|ancestor\\[(\\d+)]');
  const matches: string[] | null = string.match(ancestorRegex);
  if (matches && matches.length === 3 && !isNaN(Number(matches[2] || matches[1]))) {
    return Number(matches[2] || matches[1]);
  } else {
    return undefined;
  }
};

/**
 * Extracts the ancestor from an event property rule string. The string should contain at most one instance of
 * parent[<parentLevel>] or ancestor[<ancestorLevel>].
 * @param string
 */
export const extractAncestorFromString = (string: string) => {
  const ancestorRegex = new RegExp('(parent\\[\\d+])|(ancestor\\[(\\d+))]');
  const matches: string[] | null = string.match(ancestorRegex);
  if (matches && matches.length >= 1) {
    return matches[0];
  } else {
    return '';
  }
};

/**
 * Extracts the property name from an event property rule string. If no parent[<level>] or children[<childKey>] elements
 * are found, the string is returned indicating a property on the search items source level
 * @param string
 */
export const extractPropertyNameFromString = (string: string) => {
  const propertyNameRegex = new RegExp(
    '(?:parent\\[\\d+]|children\\[.+?]|ancestor\\[\\d+])(?:\\.(?:parent\\[\\d+]|children\\[.+?]|ancestor\\[\\d+]))*\\.(.+)',
  );
  const matches: string[] | null = string.match(propertyNameRegex);
  if (matches && matches.length === 2) {
    return matches[1];
  } else {
    return string;
  }
};

/**
 * Returns the ancestor rule for the source column's event accessor. The ancestor and child
 * level are derived from the source column's first event-property rule (see
 * {@link findEventAccessor}); we deliberately do not try to merge across multiple rules.
 *
 * Returns an empty array when the source column has no event-property rule to anchor on.
 */
export const getAncestorRules = (eventAccessor: string | undefined, levelAccessor: string) => {
  if (eventAccessor === undefined) return [];
  const ancestorLevel = extractAncestorLevelFromString(levelAccessor);
  const propertyName = SeeqNames.Properties.Id;
  return [
    {
      eventProperty: {
        propertyName: ancestorLevel !== undefined ? `${levelAccessor}.${propertyName}` : `${propertyName}`,
      },
    },
  ];
};

/**
 * Returns the property rule for the source column's event accessor.
 *
 * When `eventAccessor` is `undefined` the source column has no event-property rule (e.g. the
 * top-level item-id column on a freshly opened table); in that case we fall back to a
 * `findProperty` rule so a property-shortcut column can still be created from a plain item
 * column.
 */
export const getPropertyRules = (eventAccessor: string | undefined, name: string, columnIndex: number) => {
  if (eventAccessor === undefined) {
    return [
      {
        findProperty: {
          columnIndex,
          propertyName: `${name}`,
        },
      },
    ];
  }
  const levelPrefix = eventAccessor === '' ? '' : `${eventAccessor}.`;
  return [
    {
      eventProperty: {
        propertyName: `${levelPrefix}${name}`,
      },
    },
  ];
};

/**
 * Returns the descendant rule for the source column's event accessor. Returns an empty array
 * when the source column has no event-property rule to anchor on.
 */
export const getDescendantRules = (eventAccessor: string | undefined, child: string) => {
  if (eventAccessor === undefined) return [];
  const levelPrefix = eventAccessor === '' ? '' : `${eventAccessor}.`;
  return [
    {
      eventProperty: {
        propertyName: `${levelPrefix}children[${child}].${SeeqNames.Properties.Id}`,
      },
    },
  ];
};

export const getScalingTableTypes = (): TableTypeEnum[] => [TableTypeEnum.SCALING, TableTypeEnum.SCALINGCSV];
export const getItemTableTypes = (): TableTypeEnum[] => [TableTypeEnum.ITEM, TableTypeEnum.ITEMCSV];

/**
 * Returns the event accessor (e.g. `''`, `'parent[1]'`, `'children[Wet Bulb]'`,
 * `'ancestor[2].children[Wet Bulb]'`) used to anchor shortcut-column rules generated from a
 * source column.
 *
 * Only the *first* event-property rule on the source column is consulted; we no longer try to
 * merge across multiple fallback rules. Returns `undefined` when the source column has no
 * event-property rule (e.g. a freshly created item-id column), in which case downstream
 * `get*Rules` helpers either fall back to `findProperty` (for property shortcuts) or return
 * an empty array (for descendant/ancestor shortcuts, which need an existing event anchor).
 */
export const findEventAccessor = (mtColumnDefinition: ColumnDefinition): string | undefined => {
  const firstEventPropertyRule = mtColumnDefinition.rules.find(
    (rule) => rule.rule === SeeqNames.MaterializedTables.Rules.EventProperty,
  );
  if (!firstEventPropertyRule) return undefined;

  const property = firstEventPropertyRule.arguments[SeeqNames.MaterializedTables.Parameters.PropertyName];
  if (property === undefined) return undefined;

  const ancestorAccessor = extractAncestorFromString(property);
  const childKey = extractChildKeyFromString(property);
  if (childKey) {
    return ancestorAccessor ? `${ancestorAccessor}.children[${childKey}]` : `children[${childKey}]`;
  }
  return ancestorAccessor;
};

export interface ColumnAncestorLevel {
  level: number;
  key: string;
}

/**
 * Returns the set of ancestor levels available for "Create ancestor column" shortcuts on the
 * given source column. Like {@link findEventAccessor}, this only consults the *first*
 * event-property rule on the source column (no merging across fallbacks).
 */
export const getColumnAncestorLevels = (
  mtColumnDefinition: ColumnDefinition,
  tableType: TableTypeEnum,
  ancestorOptions: Map<string, TreeHierarchyNode> | undefined,
): Set<ColumnAncestorLevel> => {
  const firstEventPropertyRule = mtColumnDefinition.rules.find(
    (rule) => rule.rule === SeeqNames.MaterializedTables.Rules.EventProperty,
  );
  if (!firstEventPropertyRule) return new Set();

  const property = firstEventPropertyRule.arguments[SeeqNames.MaterializedTables.Parameters.PropertyName];
  if (property === undefined) return new Set();

  const searchItemsLevel =
    tableType === TableTypeEnum.SCALING
      ? 0
      : Math.max(
          ...Array.from(ancestorOptions?.keys() ?? []).map((key) => {
            return extractAncestorLevelFromString(key) ?? 0;
          }),
        ) + 1;
  const ancestorLevel = extractAncestorLevelFromString(property) ?? searchItemsLevel;
  const childKey = extractChildKeyFromString(property);
  const childModifier = tableType === TableTypeEnum.SCALING ? 1 : 0;
  const ancestorModifier = tableType === TableTypeEnum.SCALING ? -ancestorLevel : 0;

  return new Set(
    Array.from(ancestorOptions?.keys() ?? [])
      .filter((key) => {
        if (childKey) {
          return tableType === TableTypeEnum.SCALING
            ? (extractAncestorLevelFromString(key) ?? searchItemsLevel) >= ancestorLevel
            : (extractAncestorLevelFromString(key) ?? searchItemsLevel) <= ancestorLevel;
        } else {
          return tableType === TableTypeEnum.SCALING
            ? (extractAncestorLevelFromString(key) ?? searchItemsLevel) > ancestorLevel
            : (extractAncestorLevelFromString(key) ?? searchItemsLevel) < ancestorLevel;
        }
      })
      .map((key) => {
        if (childKey) {
          return {
            level: (extractAncestorLevelFromString(key) ?? searchItemsLevel) + childModifier + ancestorModifier,
            key,
          } as ColumnAncestorLevel;
        }
        return {
          level: (extractAncestorLevelFromString(key) ?? searchItemsLevel) + ancestorModifier,
          key,
        } as ColumnAncestorLevel;
      }),
  );
};

export const hasAncestorLevelOptions = (
  mtColumnDefinition: ColumnDefinition,
  tableType: TableTypeEnum,
  ancestorOptions: Map<string, TreeHierarchyNode> | undefined,
): boolean => {
  if (!mtColumnDefinition) {
    return false;
  }
  const ancestorLevelOptions: Set<ColumnAncestorLevel> = getColumnAncestorLevels(
    mtColumnDefinition,
    tableType,
    ancestorOptions,
  );
  return Array.from(ancestorLevelOptions).length > 0;
};

export const consolidatedValueFormatter = (params: { value: ProcessedTableCell; colDef?: ColDef }): string => {
  const { value, colDef } = params;
  if (isItemTableCell(value)) {
    const potentialDisplayProperty = colDef?.context?.displayProperty;
    const displayIsName = potentialDisplayProperty === SeeqNames.Properties.Name;
    const shouldShowOriginalName = potentialDisplayProperty === ORIGINAL_NAME_KEY;
    // Backend `{uuid}.display.Name` (COALESCE of join aliases then item Name) when joins are configured.
    const effectiveDisplayName = value.joinedMetadata?.displayName;
    const shouldShowEffectiveDisplayName = displayIsName && !!effectiveDisplayName && !shouldShowOriginalName;

    if (shouldShowEffectiveDisplayName) {
      return effectiveDisplayName!;
    }
    const displayProperty = shouldShowOriginalName ? SeeqNames.Properties.Name : potentialDisplayProperty;
    const fetchedProperties = value.fetchedProperties;
    const propertyValue = fetchedProperties?.[displayProperty];
    if (displayProperty && value.fetchedProperties) {
      return _.isNil(propertyValue) || propertyValue === GARBAGE_OBJECT_CHAR ? NULL_PLACEHOLDER : propertyValue;
    }

    return value?.uuid ?? NULL_PLACEHOLDER;
  }

  if (isBooleanTableCell(value)) {
    return value.value ? 'true' : 'false';
  }

  if (isNumericTableCell(value)) {
    return _.isNil(value?.value) ? NULL_PLACEHOLDER : `${value?.value}${value?.uom ?? ''}`;
  }

  if (isTextTableCell(value)) {
    return value?.value !== undefined && value.value !== '' && value.value !== GARBAGE_OBJECT_CHAR
      ? value.value
      : NULL_PLACEHOLDER;
  }

  return NULL_PLACEHOLDER;
};

export const getColumnsNotIncludedInAgGrid = (
  tableType: TableTypeEnum,
  columns: ColumnDefinitionOutputV1[],
): string[] => {
  if (!CSV_TYPES.includes(tableType)) {
    return [SeeqNames.MaterializedTables.DatumIdColumn];
  }
  if (
    CSV_TYPES.includes(tableType) &&
    columns.some((column) => column.columnName === SeeqNames.MaterializedTables.CsvItemIdColumn)
  ) {
    return [SeeqNames.MaterializedTables.ItemIdColumn];
  }

  return [];
};

export const mapPreviewApiColumnDefinitions = (columnDefinitions: ColumnDefinitionOutputV1[]): ColumnDefinition[] => {
  return columnDefinitions.map((column) => {
    const displayName = getColumnDisplayName(column.columnName, undefined);
    const displayProperty = column.columnType === ColumnTypeEnum.UUID ? column?.displayProperty : null;
    const isUUIDColumn = column.columnType === ColumnTypeEnum.UUID;
    const additionalProperties = isUUIDColumn ? DEFAULT_PROPERTIES_FOR_ALL_ITEM_COLUMNS : undefined;
    return {
      additionalProperties,
      displayName,
      ...(displayProperty !== null && { displayProperty }),
      ...column,
    };
  });
};

export const getColumnDisplayName = (columnName: string, uniqueIDColumnName: string | undefined): string => {
  if (columnName === DATUM_ID_COLUMN.columnName) {
    return uniqueIDColumnName?.trim() ? uniqueIDColumnName : t('SCALING.DEFAULT_DATUM_ID_COLUMN_NAME');
  }

  if (columnName === SeeqNames.MaterializedTables.CsvItemIdColumn) {
    return t('SCALING.DEFAULT_ITEM_ID_COLUMN_NAME');
  }

  if (columnName === ID_COLUMN.columnName) {
    return t('SCALING.DEFAULT_ITEM_ID_COLUMN_NAME');
  }

  return columnName;
};
